import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Save, ChevronDown, ChevronUp } from "lucide-react";
import { CallStatus, StockCall } from "../types";

interface AdminStockCardProps {
  callGroup: StockCall[];
  onStatusChange: (id: string, status: CallStatus) => void;
  onDelete: (id: string) => void; // used for both single call delete and group delete
  onPost: (
    groupId: string,
    updates: { exitPrices: Record<string, string>; comment: string },
  ) => void;
}

const AdminStockCard: React.FC<AdminStockCardProps> = ({
  callGroup,
  onStatusChange,
  onDelete,
  onPost,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [exitInputs, setExitInputs] = useState<Record<string, string>>({});
  const [groupComment, setGroupComment] = useState("");

  const primaryCall = callGroup[0];
  const instrumentType = primaryCall.instrumentType;
  const createdOn = new Date(primaryCall.createdOn).toLocaleTimeString(
    "en-IN",
    {
      hour: "2-digit",
      minute: "2-digit",
    },
  );

  useEffect(() => {
    const exits: Record<string, string> = {};
    callGroup.forEach((c) => {
      exits[c.id] = c.exitPrice;
    });
    setExitInputs(exits);
    setGroupComment(primaryCall.admincomment || "");
  }, [callGroup]);

  const handleToggle = () => setExpanded((s) => !s);

  const handleDeleteClick = (callId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(callId);
  };

  const handleGroupDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    // delete the whole group by passing groupId (source_recommendation_id or first call id)
    onDelete(primaryCall.source_recommendation_id || primaryCall.id);
  };

  const handlePostClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPost(primaryCall.source_recommendation_id || primaryCall.id, {
      exitPrices: exitInputs,
      comment: groupComment.trim(),
    });
  };

  const updateExit = (callId: string, value: string) => {
    setExitInputs((prev) => ({ ...prev, [callId]: value }));
  };

  const getAccentColor = (action: string) =>
    action === "BUY"
      ? "from-accent-success to-green-400"
      : "from-accent-error to-red-400";

  return (
    <motion.div
      layout
      className="bg-dark-card rounded-xl border border-dark-border overflow-hidden backdrop-blur-sm hover:shadow-xl transition"
    >
      {/* Header */}
      <div
        onClick={handleToggle}
        className="p-3 bg-gradient-to-r from-dark-surface to-dark-card border-b border-dark-border cursor-pointer flex justify-between items-center"
      >
        <div>
          <h3 className="text-sm font-semibold text-white">
            {instrumentType === "options"
              ? "Options Group"
              : primaryCall.instrumentName}
          </h3>
          <p className="text-xs text-accent-primary">{instrumentType}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{createdOn}</span>
          <span className="text-xs px-2 py-0.5 bg-accent-primary/20 text-accent-primary rounded-full font-bold">
            {callGroup.length} {callGroup.length === 1 ? "Call" : "Calls"}
          </span>
          {callGroup.length > 1 && (
            <button
              onClick={handleGroupDelete}
              className="p-1 rounded-md bg-red-500/20 hover:bg-red-500/40 transition"
            >
              <Trash2 size={16} className="text-red-400" />
            </button>
          )}
          {expanded ? (
            <ChevronUp size={16} className="text-gray-400" />
          ) : (
            <ChevronDown size={16} className="text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="p-3 bg-dark-surface space-y-3">
              {callGroup.length === 1 ? (
                // Single call layout
                callGroup.map((call) => {
                  const accentColor = getAccentColor(call.action);
                  return (
                    <motion.div
                      key={call.id}
                      className={`bg-dark-card rounded-lg border ${
                        call.status === CallStatus.ACTIONED
                          ? "border-accent-success/50 shadow-lg shadow-accent-success/10"
                          : "border-dark-border"
                      } overflow-hidden`}
                    >
                      <div className="px-3 py-2 flex justify-between items-center border-b border-dark-border">
                        <span
                          className={`font-bold text-xs bg-gradient-to-r ${accentColor} bg-clip-text text-transparent uppercase`}
                        >
                          {call.action} – {call.instrumentName}
                        </span>
                        <button
                          onClick={(e) => handleDeleteClick(call.id, e)}
                          className="p-1 rounded-md bg-red-500/20 hover:bg-red-500/40 transition"
                        >
                          <Trash2 size={14} className="text-red-400" />
                        </button>
                      </div>

                      <div className="grid grid-cols-4 gap-2 p-3 text-xs bg-dark-surface">
                        <div className="text-center">
                          <p className="text-[10px] text-gray-400 uppercase mb-1">
                            Entry
                          </p>
                          <p className="font-bold text-xs text-accent-primary">
                            ₹{call.entryPrice}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-gray-400 uppercase mb-1">
                            Target
                          </p>
                          <p className="font-bold text-xs text-accent-success">
                            ₹{call.targetPrice}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-gray-400 uppercase mb-1">
                            Stop Loss
                          </p>
                          <p className="font-bold text-xs text-accent-error">
                            ₹{call.stoploss}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-gray-400 uppercase mb-1">
                            Exit
                          </p>
                          <input
                            type="number"
                            value={exitInputs[call.id] ?? ""}
                            onChange={(e) =>
                              updateExit(call.id, e.target.value)
                            }
                            className="w-full text-center text-xs bg-white text-black rounded-md border border-dark-border px-1 py-1"
                          />
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              ) : (
                // Multi-call compact layout
                <div className="space-y-2">
                  {callGroup.map((call) => {
                    const accentColor = getAccentColor(call.action);
                    return (
                      <div
                        key={call.id}
                        className="grid grid-cols-4 gap-2 items-center text-xs bg-dark-card rounded-md border border-dark-border px-2 py-2"
                      >
                        <span className="font-semibold text-white">
                          {call.instrumentName}
                        </span>
                        <span
                          className={`font-bold text-xs bg-gradient-to-r ${accentColor} bg-clip-text text-transparent uppercase`}
                        >
                          {call.action}
                        </span>
                        <span className="text-accent-error font-bold">
                          SL: ₹{call.stoploss}
                        </span>
                        <input
                          type="number"
                          placeholder="EXIT"
                          value={exitInputs[call.id]}
                          onChange={(e) => updateExit(call.id, e.target.value)}
                          className="w-full text-center text-xs bg-white text-black rounded-md border border-dark-border px-1 py-1"
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Group Comment Section */}
              <div className="px-3 py-3 bg-dark-surface border-t border-dark-border">
                {/* Historical Comments */}
                {primaryCall.pastComments &&
                  primaryCall.pastComments.length > 0 && (
                    <div className="bg-dark-card rounded-md border border-dark-border p-2 text-xs text-gray-300">
                      <p className="font-semibold text-gray-400 mb-1">
                        What Says Vytta
                      </p>
                      <div className="space-y-1 max-h-24 overflow-y-auto">
                        {primaryCall.pastComments}
                      </div>
                    </div>
                  )}
                <br />
                <textarea
                  value={""}
                  onChange={(e) => setGroupComment(e.target.value)}
                  placeholder="Write group admin comment..."
                  className="w-full  text-xs bg-dark-card rounded-md border text-black border-dark-border p-2 resize-none"
                  rows={2}
                />
                <button
                  onClick={handlePostClick}
                  className="mt-2 px-3 py-1.5 text-xs bg-blue-500 text-white rounded-md flex items-center gap-1 hover:bg-blue-600 transition"
                >
                  <Save size={12} /> Post All Exits & Comment
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AdminStockCard;
